/**
 * Mappers para convertir entre formato de la app (camelCase)
 * y formato de Supabase (snake_case)
 */

import type { CompletionRecord, DaySnapshot, LayerMetrics } from '@/core/types';
import type { UserAchievementState } from '@/services/achievements';

// =====================================================
// Tipos para Supabase (snake_case)
// =====================================================

export interface SupabaseCompletion {
  id: string;
  protocol_id: string;
  protocol_title: string;
  frequency: string;
  layer: string;
  completed_at: string;
  date: string;
  time_estimate: number;
  completed_subtasks: string[];
  total_subtasks: number;
  is_partial: boolean;
  time_block: string | null;
  mode: string;
  sync_status: string;
  last_modified: string;
}

export interface SupabaseDaySnapshot {
  date: string;
  year: number;
  month: number;
  week_of_year: number;
  day_of_week: number;
  is_weekend: boolean;
  survival: LayerMetrics;
  maintenance: LayerMetrics;
  growth: LayerMetrics;
  total_protocols: number;
  completed_protocols: number;
  completion_rate: number;
  total_time_estimate: number;
  completed_time_estimate: number;
  used_hard_day_mode: boolean;
  used_weekend_mode: boolean;
  energy_level: number | null;
  mood_level: number | null;
  notes: string | null;
  sync_status: string;
  last_modified: string;
}

export interface SupabaseAchievementState {
  id: string;
  unlocked_ids: string[];
  last_unlocked: string | null;
  current_streak: number;
  total_completions: number;
  perfect_days: number;
  hard_days_recovered: number;
  features_used: string[];
  sync_status: string;
  last_modified: string;
}

// =====================================================
// Mappers: App -> Supabase
// =====================================================

export function completionToSupabase(record: CompletionRecord): SupabaseCompletion {
  return {
    id: record.id,
    protocol_id: record.protocolId,
    protocol_title: record.protocolTitle,
    frequency: record.frequency,
    layer: record.layer,
    completed_at: record.completedAt,
    date: record.date,
    time_estimate: record.timeEstimate,
    completed_subtasks: record.completedSubtasks,
    total_subtasks: record.totalSubtasks,
    is_partial: record.isPartial,
    time_block: record.timeBlock ?? null,
    mode: record.mode,
    sync_status: record.syncStatus,
    last_modified: record.lastModified,
  };
}

export function snapshotToSupabase(snapshot: DaySnapshot): SupabaseDaySnapshot {
  return {
    date: snapshot.date,
    year: snapshot.year,
    month: snapshot.month,
    week_of_year: snapshot.weekOfYear,
    day_of_week: snapshot.dayOfWeek,
    is_weekend: snapshot.isWeekend,
    survival: snapshot.survival,
    maintenance: snapshot.maintenance,
    growth: snapshot.growth,
    total_protocols: snapshot.totalProtocols,
    completed_protocols: snapshot.completedProtocols,
    completion_rate: snapshot.completionRate,
    total_time_estimate: snapshot.totalTimeEstimate,
    completed_time_estimate: snapshot.completedTimeEstimate,
    used_hard_day_mode: snapshot.usedHardDayMode,
    used_weekend_mode: snapshot.usedWeekendMode,
    energy_level: snapshot.energyLevel ?? null,
    mood_level: snapshot.moodLevel ?? null,
    notes: snapshot.notes ?? null,
    sync_status: snapshot.syncStatus,
    last_modified: snapshot.lastModified,
  };
}

// =====================================================
// Mappers: Supabase -> App
// =====================================================

export function completionFromSupabase(data: SupabaseCompletion): CompletionRecord {
  const record: CompletionRecord = {
    id: data.id,
    protocolId: data.protocol_id,
    protocolTitle: data.protocol_title,
    frequency: data.frequency as CompletionRecord['frequency'],
    layer: data.layer as CompletionRecord['layer'],
    completedAt: data.completed_at,
    date: data.date,
    timeEstimate: data.time_estimate,
    completedSubtasks: data.completed_subtasks,
    totalSubtasks: data.total_subtasks,
    isPartial: data.is_partial,
    mode: data.mode as CompletionRecord['mode'],
    syncStatus: data.sync_status as CompletionRecord['syncStatus'],
    lastModified: data.last_modified,
  };

  // timeBlock es opcional
  if (data.time_block !== null) {
    (record as { timeBlock?: 'morning' | 'afternoon' | 'evening' }).timeBlock =
      data.time_block as 'morning' | 'afternoon' | 'evening';
  }

  return record;
}

export function snapshotFromSupabase(data: SupabaseDaySnapshot): DaySnapshot {
  const snapshot: DaySnapshot = {
    date: data.date,
    year: data.year,
    month: data.month,
    weekOfYear: data.week_of_year,
    dayOfWeek: data.day_of_week,
    isWeekend: data.is_weekend,
    survival: data.survival,
    maintenance: data.maintenance,
    growth: data.growth,
    totalProtocols: data.total_protocols,
    completedProtocols: data.completed_protocols,
    completionRate: data.completion_rate,
    totalTimeEstimate: data.total_time_estimate,
    completedTimeEstimate: data.completed_time_estimate,
    usedHardDayMode: data.used_hard_day_mode,
    usedWeekendMode: data.used_weekend_mode,
    syncStatus: data.sync_status as DaySnapshot['syncStatus'],
    lastModified: data.last_modified,
  };

  // Campos opcionales
  if (data.energy_level !== null) {
    snapshot.energyLevel = data.energy_level;
  }
  if (data.mood_level !== null) {
    snapshot.moodLevel = data.mood_level;
  }
  if (data.notes !== null) {
    snapshot.notes = data.notes;
  }

  return snapshot;
}

// =====================================================
// Mappers: Achievements
// =====================================================

export function achievementStateToSupabase(state: UserAchievementState): SupabaseAchievementState {
  return {
    id: 'main',
    unlocked_ids: state.unlockedIds,
    last_unlocked: state.lastUnlocked,
    current_streak: state.stats.currentStreak,
    total_completions: state.stats.totalCompletions,
    perfect_days: state.stats.perfectDays,
    hard_days_recovered: state.stats.hardDaysRecovered,
    features_used: state.stats.featuresUsed,
    sync_status: 'synced',
    last_modified: new Date().toISOString(),
  };
}

export function achievementStateFromSupabase(data: SupabaseAchievementState): UserAchievementState {
  return {
    unlockedIds: data.unlocked_ids,
    lastUnlocked: data.last_unlocked,
    stats: {
      currentStreak: data.current_streak,
      totalCompletions: data.total_completions,
      perfectDays: data.perfect_days,
      hardDaysRecovered: data.hard_days_recovered,
      featuresUsed: data.features_used,
    },
  };
}
